"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.createConfiguration = createConfiguration;
exports.exportedForTesting = void 0;
var _amazonChimeSdkJs = require("amazon-chime-sdk-js");
var _StudioLogger = require("@studio/logger/StudioLogger");
var _StudioVideoUplinkBandwidthPolicy = require("../customPolicies/StudioVideoUplinkBandwidthPolicy");
var logger = new _StudioLogger.StudioLogger('chime-sdk-events');

/**
 * Creates a priority-based downlink policy.
 *
 * This function logs the usage of the Video Based Priority Policy for the session and then creates a new instance of the VideoPriorityBasedPolicy class.
 * The policy configuration is set to use bandwidth probing and remote video quality adaption for server-side network adaption.
 *
 * @param {ConsoleLogger} logger - The logger instance used for logging.
 * @returns {VideoPriorityBasedPolicy} - The created VideoPriorityBasedPolicy instance.
 */
function createPriorityBasedDownlinkPolicy(logger) {
  // Log the usage of Video Based Priority Policy for the session
  var title = 'Using Video Based Priority Policy for Session';
  logger.info(title, {
    title: title
  });

  // Configure the policy for server-side network adaptation
  var videoPriorityBasedPolicyConfig = new _amazonChimeSdkJs.VideoPriorityBasedPolicyConfig();
  videoPriorityBasedPolicyConfig.serverSideNetworkAdaption = _amazonChimeSdkJs.ServerSideNetworkAdaption.BandwidthProbingAndRemoteVideoQualityAdaption;

  // Create and return the VideoPriorityBasedPolicy instance
  return new _amazonChimeSdkJs.VideoPriorityBasedPolicy(logger, videoPriorityBasedPolicyConfig);
}
/**
 * Adds a policy observer to the provided priority-based downlink policy.
 *
 * This function creates a policy observer that logs when a tile will be paused or unpause by the downlink policy.
 * The observer is then added to the priority-based downlink policy, and the policy is set as the video downlink bandwidth policy in the provided configuration.
 *
 * @param {VideoPriorityBasedPolicy} priorityBasedDownlinkPolicy - The priority-based downlink policy.
 * @param {Object} meetingResponse - The meeting response object.
 */
function addPolicyObserver(priorityBasedDownlinkPolicy, meetingResponse) {
  // Helper function to log tile policy events to Datadog
  // eslint-disable-next-line func-style
  var logTilePolicyEvent = function logTilePolicyEvent(action, tileId) {
    var meetingId = meetingResponse.Meeting.MeetingId;
    var logPayload = {
      'studio.meeting.id': meetingId,
      title: "Chime tileWillBe".concat(action, "ByDownlinkPolicy: ").concat(tileId)
    };
    logger.info(logPayload.title, logPayload);
  };
  var policyObserver = {
    tileWillBePausedByDownlinkPolicy: function tileWillBePausedByDownlinkPolicy(tileId) {
      return logTilePolicyEvent('Paused', tileId);
    },
    tileWillBeUnpausedByDownlinkPolicy: function tileWillBeUnpausedByDownlinkPolicy(tileId) {
      return logTilePolicyEvent('UnPaused', tileId);
    }
  };
  priorityBasedDownlinkPolicy.addObserver(policyObserver);
}
function applyServerSideNetworkAdaption(configuration, logger, meetingResponse) {
  var priorityBasedDownlinkPolicy = createPriorityBasedDownlinkPolicy(logger);
  addPolicyObserver(priorityBasedDownlinkPolicy, meetingResponse);
  configuration.videoDownlinkBandwidthPolicy = priorityBasedDownlinkPolicy;
}
function configureVideoQuality(meetingSession) {
  meetingSession.audioVideo.chooseVideoInputQuality(1280, 720, 30);
  meetingSession.audioVideo.setVideoMaxBandwidthKbps(1400);
}

/**
 * Configures video codec preferences in chime for the meeting session.
 *
 * Explanation of Codec Choices and Fallback Sequence
 * 1. VP9:
 *    - VP9 is a high-efficiency video codec that provides better compression and quality compared to older codecs like H.264.
 *    - It is preferred for its superior performance but may not be supported on all devices and browsers, especially older ones.
 * 2. H.264 Baseline Profile:
 *    - H.264 Baseline Profile is widely supported and offers good compatibility with hardware acceleration.
 *    - It is a reliable fallback option due to its broad compatibility, though it is less efficient than VP9.
 * 3. H.264:
 *    - This represents the main profile of H.264, but has less hardware acceleration support compared to the Baseline Profile.
 *    - It is used as a fallback when VP9 and H.264 Baseline Profile are not available.
 * 4. H.264 Constrained Baseline Profile:
 *    - This variant of the H.264 codec is designed to be more compatible with constrained environments, such as low-power devices.
 *    - It is included as a fallback to ensure compatibility with a wider range of devices.
 * 5. VP8:
 *    - VP8 is an older codec compared to VP9 but still offers good compression and quality.
 *    - It is widely supported across different browsers and devices, making it a reliable fallback option when other codecs are not supported.
 *
 * https://aws.github.io/amazon-chime-sdk-js/classes/videocodeccapability.html
 *
 * @param {DefaultMeetingSession} meetingSession The meeting session instance.
 */
function configureVideoCodecPreferences(meetingSession) {
  meetingSession.audioVideo.setVideoCodecSendPreferences([_amazonChimeSdkJs.VideoCodecCapability.vp9(), _amazonChimeSdkJs.VideoCodecCapability.h264BaselineProfile(), _amazonChimeSdkJs.VideoCodecCapability.h264(), _amazonChimeSdkJs.VideoCodecCapability.h264ConstrainedBaselineProfile(), _amazonChimeSdkJs.VideoCodecCapability.vp8()]);
  meetingSession.audioVideo.setContentShareVideoCodecPreferences([_amazonChimeSdkJs.VideoCodecCapability.av1(), _amazonChimeSdkJs.VideoCodecCapability.h264BaselineProfile(), _amazonChimeSdkJs.VideoCodecCapability.h264(), _amazonChimeSdkJs.VideoCodecCapability.h264ConstrainedBaselineProfile(), _amazonChimeSdkJs.VideoCodecCapability.vp8()]);
}

/**
 * Util function to get Chime meeting session
 * @param meetingResponse
 * @param attendeeResponse
 * @returns {{meetingResponse, meetingSession: DefaultMeetingSession, attendeeResponse}}
 */
function createConfiguration(meetingResponse, attendeeResponse) {
  var _configuration$creden;
  var logger = new _amazonChimeSdkJs.ConsoleLogger('ChimeMeetingLogger', _amazonChimeSdkJs.LogLevel.WARN);
  var deviceController = new _amazonChimeSdkJs.DefaultDeviceController(logger);
  var configuration = new _amazonChimeSdkJs.MeetingSessionConfiguration(meetingResponse, attendeeResponse);
  configuration.keepLastFrameWhenPaused = true;
  configuration.enableSVC = true;
  var uplinkPolicy = new _StudioVideoUplinkBandwidthPolicy.StudioVideoUplinkBandwidthPolicy((_configuration$creden = configuration.credentials) === null || _configuration$creden === void 0 ? void 0 : _configuration$creden.attendeeId, true, logger);
  uplinkPolicy.setSVCEnabled(true);
  configuration.videoUplinkBandwidthPolicy = uplinkPolicy;
  applyServerSideNetworkAdaption(configuration, logger, meetingResponse);
  deviceController.eventController = new _amazonChimeSdkJs.DefaultEventController(configuration, logger);
  var meetingSession = new _amazonChimeSdkJs.DefaultMeetingSession(configuration, logger, deviceController);
  configureVideoCodecPreferences(meetingSession);
  configureVideoQuality(meetingSession);
  return {
    meetingResponse: meetingResponse,
    meetingSession: meetingSession,
    attendeeResponse: attendeeResponse
  };
}
var exportedForTesting = exports.exportedForTesting = {
  addPolicyObserver: addPolicyObserver
};